import * as colors from "../../constants/colors";
import * as languages from "../../constants/languages";
import { english, spanish } from "../../constants/strings";
import { css, StyleSheet } from "aphrodite";
import * as breakpoints from "../../breakpoints";
import { Container } from "../../grid";
import uuidv4 from "uuid/v4";
import Page from "../Page";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import { getLiveClassesSchedule } from "../../api/class";
import { format, startOfWeek, endOfWeek } from "date-fns";
import { drupalSiteUrl } from "../../config";
import LiveSessionModal from "../LiveSessionModal";
import * as es from "date-fns/locale/es";
import * as en from "date-fns/locale/en";

const styles = StyleSheet.create({
	contentContainer: {
		marginTop: 16,
		[breakpoints.mobileOnly]: {
			marginTop: 20,
		},
		flex: 1,
	},
	mobileCalendar: {
		[breakpoints.tabletAndUp]: {
			display: "none",
		},
	},
	calendar: {
		[breakpoints.mobileOnly]: {
			display: "none",
		},
	},
	weeks: {
		border: `1px solid ${colors.white}`,
		borderCollapse: "collapse",
		width: "100%",
		height: 400,
		fontFamily: "priori-sans",
	},
	day: {
		border: `2px solid ${colors.white}`,
		backgroundColor: colors.gray3,
		verticalAlign: "top",
		width: "14%",
		height: "48%",
		fontSize: 18,
		paddingLeft: 8,
		paddingRight: 8,
	},
	date: {
		marginBottom: 12,
	},
	dayOfWeek: {
		border: `2px solid ${colors.white}`,
		backgroundColor: colors.teal,
		verticalAlign: "top",
		width: "14%",
		textAlign: "center",
		fontWeight: "bold",
		color: colors.white,
	},
	month: {
		fontFamily: "priori-sans",
	},
	description: {
		fontFamily:
			"TrebuchetMS, Trebuchet MS, Lucida Grande, Lucida Sans Unicode, Lucida Sans, Tahoma,sans-serif",
	},
	highlighted: {
		backgroundColor: colors.tealHighlight,
	},
	padding: {
		paddingleft: 8,
	},
	className: {
		marginBottom: 20,
		fontWeight: "bold",
		textDecoration: "underline",
		[breakpoints.desktopOnly]: {
			":hover": {
				cursor: "pointer",
			},
		},
	},
	lightClassName: {
		marginBottom: 20,
	},
	header: {
		backgroundColor: colors.teal,
		fontWeight: "bold",
		color: colors.white,
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between",
		paddingTop: 8,
		paddingBottom: 8,
		paddingLeft: 16,
		paddingRight: 16,
		marginTop: 24,
	},
	mobileDay: {
		paddingTop: 8,
		paddingBottom: 8,
		paddingLeft: 16,
		paddingRight: 16,
		backgroundColor: colors.tealHighlight,
		fontWeight: "bold",
	},
	mainButton: {
		border: `3px solid ${colors.darkTeal2}`,
		borderRadius: 16,
		color: colors.darkGreen,
		width: 168,
		height: 50,
		fontWeight: "bold",
		fontFamily: "priori-sans",
		fontSize: 18,
		lineHeight: 0.94,
	},
	buttonContainer: {
		marginTop: 24,
		textAlign: "center",
	},
});

const startDate = startOfWeek(Date.now());
const dateFormat = "YYYY-MM-DD";

const daysOfWeek = ["SUN", "MON", "TUES", "WED", "THU", "FRI", "SAT"];
const daysOfWeekEs = ["DO", "LU", "MA", "MI", "JU", "VI", "SA"];

export default class LiveCalendarPage extends PureComponent {
	static propTypes = {
		params: PropTypes.object.isRequired,
		language: PropTypes.string.isRequired,
		pathname: PropTypes.string.isRequired,
		loggedIn: PropTypes.bool,
	};

	constructor(props) {
		super(props);
		this.strings = props.language === languages.es ? spanish : english;
		this.state = {
			coursesMap: {},
			showModal: false,
			selectedClass: {
				name: "",
				url: "",
				description: "",
				startTime: new Date(),
			},
		};
	}

	componentDidMount() {
		this._getLiveClasses();
	}

	_dismissModal = () => {
		this.setState({
			showModal: false,
		});
	};

	_sessionClicked = session => {
		this.setState({
			showModal: true,
			selectedClass: {
				name: session.name,
				description: session.description,
				url: `/class/${session.id}?language=${this.props.language}`,
				startTime: new Date(session.startTime),
			},
		});
	};

	_getLiveClasses = () => {
		let endDate = endOfWeek(startDate);
		endDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() + 7);
		getLiveClassesSchedule(
			format(startDate, dateFormat),
			format(endDate, dateFormat),
			this.props.language,
		)
			.then(data => {
				this.setState({
					coursesMap: data,
				});
			})
			.catch(err => {
				console.log(err);
			});
	};
	_renderWeek = start => {
		const week = [];
		for (let i = 0; i < 7; i++) {
			const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
			const classes = this.state.coursesMap[format(date, dateFormat)]
				? this.state.coursesMap[format(date, dateFormat)]
				: [];
			const today = new Date();
			const active = date >= new Date(today.getFullYear(), today.getMonth(), today.getDate());
			week.push(
				<td
					key={date}
					className={css([
						styles.day,
						classes.length > 0 && active ? styles.highlighted : null,
					])}
				>
					<div>
						<div className={css(styles.date)}>{date.getDate()}</div>
						{classes.map(c => {
							if (active) {
								return (
									<div
										key={c.id}
										className={css(styles.className)}
										onClick={e => {
											e.preventDefault();
											this._sessionClicked(c);
										}}
									>
										{c.name}
									</div>
								);
							} else {
								return (
									<div key={c.id} className={css(styles.lightClassName)}>
										{c.name}
									</div>
								);
							}
						})}
					</div>
				</td>,
			);
		}
		return week;
	};

	_renderDaysOfWeek = () => {
		let dow = this.props.language === languages.es ? daysOfWeekEs : daysOfWeek;
		return dow.map(day => (
			<td key={day} className={css(styles.dayOfWeek)}>
				<div>{day}</div>
			</td>
		));
	};

	_renderWeekMobile = start => {
		const week = [];
		for (let i = 0; i < 14; i++) {
			const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
			week.push(format(date, dateFormat));
		}

		return week.filter(dateStr => this.state.coursesMap[dateStr]).map(dateStr => {
			const classes = this.state.coursesMap[dateStr];
			let date = new Date(dateStr);
			if (classes.length > 0) {
				date = new Date(classes[0].startTime);
			}
			const today = new Date();

			return (
				<div key={uuidv4()}>
					<div className={css(styles.header)}>
						<div>{format(date, "MMMM DD").toUpperCase()}</div>
						<div>{format(date, "ddd").toUpperCase()}</div>
					</div>
					{classes.map(
						c =>
							new Date(c.startTime) > today ? (
								<div key={c.id} className={css(styles.mobileDay)}>
									<div
										key={c.id}
										className={css(styles.className)}
										onClick={e => {
											e.preventDefault();
											this._sessionClicked(c);
										}}
									>
										{c.name}
									</div>
								</div>
							) : (
								<div key={c.id} className={css(styles.mobileDay)}>
									{c.name}
								</div>
							),
					)}
				</div>
			);
		});
	};

	_returnToCatalog = () => {
		window.location.assign(
			`${drupalSiteUrl}${
				this.props.language === languages.es ? "/es/clases-en-linea" : "/online-classes"
			}`,
		);
	};

	render() {
		const locale = { locale: this.props.language === languages.es ? es : en };
		return (
			<Page
				activeTab={this.strings.tabNames.CATALOG}
				currentRoute={this.props.pathname}
				pageTitle={this.strings.liveClassCalendar}
				language={this.props.language}
				loggedIn={this.props.loggedIn}
			>
				<Container style={css(styles.contentContainer)}>
					<div className={css(styles.description)}>
						{this.strings.calendarDescription}
					</div>
					<div className={css(styles.mobileCalendar)}>
						{this._renderWeekMobile(startDate)}
					</div>
					<div className={css(styles.calendar)}>
						<h2 className={css(styles.month)}>{format(startDate, "MMMM", locale)}</h2>
						<table className={css(styles.weeks)}>
							<thead>
								<tr>{this._renderDaysOfWeek()}</tr>
							</thead>
							<tbody>
								<tr className={css(styles.week)}>{this._renderWeek(startDate)}</tr>
								<tr className={css(styles.week)}>
									{this._renderWeek(
										new Date(
											startDate.getFullYear(),
											startDate.getMonth(),
											startDate.getDate() + 7,
										),
									)}
								</tr>
							</tbody>
						</table>
					</div>
					<div className={css(styles.buttonContainer)}>
						<button onClick={this._returnToCatalog} className={css(styles.mainButton)}>
							{this.strings.returnToCatalog}
						</button>
					</div>
				</Container>
				<LiveSessionModal
					showing={this.state.showModal}
					dismiss={this._dismissModal}
					language={this.props.language}
					title={this.state.selectedClass.name}
					description={this.state.selectedClass.description}
					url={this.state.selectedClass.url}
					startTime={this.state.selectedClass.startTime}
				/>
			</Page>
		);
	}
}



// WEBPACK FOOTER //
// ./src/components/pages/LiveCalendarPage.js